import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Car, CreditCard, Loader2, AlertCircle, CheckCircle, Clock } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Mobile Payment Landing Page
 * 
 * Purpose: Static wall QR code landing page for customers to pay parking fees on their phone.
 * 
 * Flow:
 * 1. Customer scans wall QR code → arrives at /pay
 * 2. Customer enters VRM (or pre-filled from URL param)
 * 3. Check Fee → calls GET /api/parking/tariff/{vrm}
 * 4. Displays amount due
 * 5. Pay Before Exit → calls POST /api/payment/mobile/create
 * 6. Redirect to Stripe Checkout
 * 7. Stripe webhook handles payment → exit auth created
 */
export default function MobilePay() {
  const [searchParams] = useSearchParams();
  const [vrm, setVrm] = useState(searchParams.get("vrm") || "");
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [tariffData, setTariffData] = useState(null);
  const [error, setError] = useState(null);
  const [cancelled, setCancelled] = useState(searchParams.get("cancelled") === "true");

  // Auto-check fee if VRM is pre-filled
  useEffect(() => {
    const prefillVrm = searchParams.get("vrm");
    if (prefillVrm && prefillVrm.length >= 5) {
      handleCheckFee(prefillVrm);
    }
  }, []);

  const handleCheckFee = async (vrmToCheck = vrm) => {
    const cleanVrm = vrmToCheck.trim().toUpperCase().replace(/\s/g, "");
    if (!cleanVrm || cleanVrm.length < 4) {
      setError("Please enter a valid vehicle registration");
      return;
    }

    setLoading(true);
    setError(null);
    setTariffData(null);
    setCancelled(false);

    try {
      const response = await axios.get(`${API}/parking/tariff/${encodeURIComponent(cleanVrm)}`);
      const data = response.data;

      if (!data.found) {
        setError(data.message || "No active parking session found for this registration");
        return;
      }

      setTariffData(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No active parking session found for this registration");
      } else {
        setError("Unable to check your parking fee. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!tariffData?.session_id) return;

    setPaying(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/payment/mobile/create`, {
        session_id: tariffData.session_id,
        origin_url: window.location.origin
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setError("Failed to create payment session");
        setPaying(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start payment. Please try again.");
      setPaying(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "0m";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div
      data-testid="mobile-pay-page"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0d2137 0%, #1e3a5f 100%)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "20px",
        boxSizing: "border-box"
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <img
          src="/parkwise-logo.png"
          alt="ParkWise"
          style={{ height: "48px", marginBottom: "12px" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <h1 style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "white",
          margin: "0 0 4px 0"
        }}>
          Pay for Parking
        </h1>
        <p style={{ fontSize: "14px", color: "#94a3b8", margin: 0 }}>
          Enter your registration to check your fee
        </p>
      </div>

      {/* Cancelled Notice */}
      {cancelled && !tariffData && (
        <div style={{
          background: "rgba(251, 191, 36, 0.1)",
          border: "1px solid #fbbf24",
          borderRadius: "12px",
          padding: "12px 16px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}>
          <AlertCircle size={20} color="#fbbf24" />
          <span style={{ color: "#fbbf24", fontSize: "14px" }}>
            Payment cancelled. You can try again.
          </span>
        </div>
      )}

      {/* VRM Input Card */}
      <div style={{
        background: "white",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
      }}>
        <label style={{
          display: "block",
          fontSize: "14px",
          fontWeight: "600",
          color: "#374151",
          marginBottom: "8px"
        }}>
          Vehicle Registration
        </label>

        {/* UK Number Plate Style Input */}
        <div style={{
          background: "#fef08a",
          borderRadius: "8px",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          border: "3px solid #eab308",
          marginBottom: "16px"
        }}>
          <div style={{
            background: "#1e40af",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "700"
          }}>GB</div>
          <input
            type="text"
            value={vrm}
            onChange={(e) => {
              setVrm(e.target.value.toUpperCase());
              setError(null);
              setTariffData(null);
            }}
            placeholder="AB12 CDE"
            maxLength={8}
            data-testid="vrm-input"
            style={{
              flex: 1,
              border: "none",
              background: "transparent",
              fontSize: "24px",
              fontWeight: "700",
              color: "#1e293b",
              textAlign: "center",
              letterSpacing: "4px",
              fontFamily: "monospace",
              outline: "none"
            }}
          />
        </div>

        {/* Check Fee Button */}
        <button
          onClick={() => handleCheckFee()}
          disabled={loading || vrm.length < 4}
          data-testid="check-fee-button"
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: "10px",
            border: "none",
            background: loading || vrm.length < 4 ? "#94a3b8" : "#1e3a5f",
            color: "white",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading || vrm.length < 4 ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}
        >
          {loading ? (
            <>
              <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
              Checking...
            </>
          ) : (
            <>
              <Car size={20} />
              Check Fee
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          data-testid="error-message"
          style={{
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid #ef4444",
            borderRadius: "12px",
            padding: "14px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}
        >
          <AlertCircle size={20} color="#ef4444" />
          <span style={{ color: "#ef4444", fontSize: "14px" }}>{error}</span>
        </div>
      )}

      {/* Tariff Result Card */}
      {tariffData && (
        <div
          data-testid="tariff-result"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
          }}
        >
          {/* Already Paid */}
          {tariffData.is_settled && tariffData.has_exit_auth ? (
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: "64px",
                height: "64px",
                background: "#22c55e",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px"
              }}>
                <CheckCircle size={32} color="white" />
              </div>
              <h3 style={{ fontSize: "20px", fontWeight: "700", color: "#22c55e", margin: "0 0 8px" }}>
                Already Paid
              </h3>
              <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
                Your parking session is paid. You may exit the car park.
              </p>
            </div>
          ) : tariffData.payment_required ? (
            <>
              {/* Session Details */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px"
                }}>
                  <span style={{ color: "#64748b", fontSize: "14px" }}>Registration</span>
                  <span style={{ fontWeight: "700", color: "#1e293b", fontSize: "16px" }}>
                    {tariffData.vrm}
                  </span>
                </div>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px"
                }}>
                  <span style={{ color: "#64748b", fontSize: "14px" }}>Duration</span>
                  <span style={{
                    fontWeight: "600",
                    color: "#1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <Clock size={16} color="#64748b" />
                    {formatDuration(tariffData.duration_minutes)}
                  </span>
                </div>
                <div style={{
                  height: "1px",
                  background: "#e5e7eb",
                  margin: "16px 0"
                }} />
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <span style={{ color: "#374151", fontSize: "16px", fontWeight: "600" }}>
                    Amount Due
                  </span>
                  <span style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#16a34a"
                  }}>
                    £{tariffData.amount_due?.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Pay Before Exit Button */}
              <button
                onClick={handlePayNow}
                disabled={paying}
                data-testid="pay-before-exit-button"
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "none",
                  background: paying ? "#94a3b8" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "700",
                  cursor: paying ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  boxShadow: "0 4px 12px rgba(22, 163, 74, 0.3)"
                }}
              >
                {paying ? (
                  <>
                    <Loader2 size={22} style={{ animation: "spin 1s linear infinite" }} />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard size={22} />
                    Pay Before Exit
                  </>
                )}
              </button>

              <p style={{
                textAlign: "center",
                color: "#94a3b8",
                fontSize: "12px",
                margin: "12px 0 0"
              }}>
                Secure payment powered by Stripe
              </p>
            </>
          ) : (
            /* No Payment Required */
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: "64px",
                height: "64px",
                background: "#3b82f6",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px"
              }}>
                <Car size={32} color="white" />
              </div>
              <h3 style={{ fontSize: "20px", fontWeight: "700", color: "#1e3a5f", margin: "0 0 8px" }}>
                No Payment Required
              </h3>
              <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
                {tariffData.message || "Your parking session is within the free period."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Help Footer */}
      <div style={{
        textAlign: "center",
        marginTop: "24px",
        color: "#94a3b8",
        fontSize: "13px"
      }}>
        <p style={{ margin: "0 0 4px" }}>Need help?</p>
        <a
          href="tel:01onal292833107"
          style={{ color: "#fbbf24", fontWeight: "600", textDecoration: "none" }}
        >
          Call 01292 833107
        </a>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
